/**
 * Batch Processes Shared Handlers
 */

import { BatchProcessesSharedState } from './state';
import { BatchProcessesSharedCommand } from './commands';
import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';

type ExtractCommand<T extends string> = Extract<BatchProcessesSharedCommand, { type: T }>;

// Subscription handlers

const handleSetProcesses = (
  state: BatchProcessesSharedState,
  cmd: ExtractCommand<'batchProcesses/shared/subscription/setProcesses'>
): BatchProcessesSharedState => ({
  ...state,
  activeProcesses: cmd.payload.processes
});

const handleUpsertProcess = (
  state: BatchProcessesSharedState,
  cmd: ExtractCommand<'batchProcesses/shared/subscription/upsertProcess'>
): BatchProcessesSharedState => {
  const { process } = cmd.payload;
  const existingIndex = state.activeProcesses.findIndex( p => p.id === process.id );

  if ( existingIndex >= 0 ) {
    const updated = [...state.activeProcesses];
    updated[existingIndex] = process;
    return { ...state, activeProcesses: updated };
  }

  return { ...state, activeProcesses: [ ...state.activeProcesses, process ]};
};

const handleRemoveProcess = (
  state: BatchProcessesSharedState,
  cmd: ExtractCommand<'batchProcesses/shared/subscription/removeProcess'>
): BatchProcessesSharedState => ({
  ...state,
  activeProcesses: state.activeProcesses.filter( p => p.id !== cmd.payload.processId )
});

// Progress handlers (TV1-1979)

const handleSetProcessProgress = (
  state: BatchProcessesSharedState,
  cmd: ExtractCommand<'batchProcesses/shared/progress/setProcessProgress'>
): BatchProcessesSharedState => {
  const { processId, projectedCount } = cmd.payload;
  return {
    ...state,
    progressByProcessId: {
      ...state.progressByProcessId,
      [processId]: { projectedCount, loading: false }
    }
  };
};

const handleSetProcessProgressLoading = (
  state: BatchProcessesSharedState,
  cmd: ExtractCommand<'batchProcesses/shared/progress/setProcessProgressLoading'>
): BatchProcessesSharedState => {
  const { processId, loading } = cmd.payload;
  const existing = state.progressByProcessId[processId];
  return {
    ...state,
    progressByProcessId: {
      ...state.progressByProcessId,
      [processId]: { projectedCount: existing?.projectedCount ?? 0, loading }
    }
  };
};

const handleRemoveProcessProgress = (
  state: BatchProcessesSharedState,
  cmd: ExtractCommand<'batchProcesses/shared/progress/removeProcessProgress'>
): BatchProcessesSharedState => {
  const { [cmd.payload.processId]: _removed, ...rest } = state.progressByProcessId;
  return { ...state, progressByProcessId: rest };
};

// Credential handlers (TV1-2446 slice 6)

const handleSetRevokedChannelTargets = (
  state: BatchProcessesSharedState,
  cmd: ExtractCommand<'batchProcesses/shared/credentials/setRevokedChannelTargets'>
): BatchProcessesSharedState => ({
  ...state,
  revokedChannelTargets: cmd.payload.integrationIds
});

// Dropdown handlers

const handleDropdownOpen = (
  state: BatchProcessesSharedState,
  _cmd: ExtractCommand<'batchProcesses/shared/dropdown/open'>
): BatchProcessesSharedState => ({
  ...state,
  dropdownOpen: true
});

const handleDropdownClose = (
  state: BatchProcessesSharedState,
  _cmd: ExtractCommand<'batchProcesses/shared/dropdown/close'>
): BatchProcessesSharedState => ({
  ...state,
  dropdownOpen: false
});

export const handlers: CommandHandlerMap<BatchProcessesSharedState, BatchProcessesSharedCommand> = {
  'batchProcesses/shared/subscription/setProcesses': handleSetProcesses,
  'batchProcesses/shared/subscription/upsertProcess': handleUpsertProcess,
  'batchProcesses/shared/subscription/removeProcess': handleRemoveProcess,
  'batchProcesses/shared/progress/setProcessProgress': handleSetProcessProgress,
  'batchProcesses/shared/progress/setProcessProgressLoading': handleSetProcessProgressLoading,
  'batchProcesses/shared/progress/removeProcessProgress': handleRemoveProcessProgress,
  'batchProcesses/shared/credentials/setRevokedChannelTargets': handleSetRevokedChannelTargets,
  'batchProcesses/shared/dropdown/open': handleDropdownOpen,
  'batchProcesses/shared/dropdown/close': handleDropdownClose
};
